import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, X, Stamp, Sparkles } from "lucide-react";
import Marquee from "react-fast-marquee";
import { api } from "@/lib/api";
import { LANE_CATEGORIES, LANE_THEMES, productLane } from "@/lib/laneTheme";
import SEO from "@/components/SEO";
import {
  SquigglyUnderline,
  UpsettingDog,
  BadFrog,
  Scribble,
  StarBlob,
  HandArrow,
} from "@/components/Doodle";

export default function Shop({ filterNew = false }) {
  const [products, setProducts] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const lane = searchParams.get("lane");
  const theme = lane && LANE_THEMES[lane] ? LANE_THEMES[lane] : null;
  const isDamp = lane === "damp";
  const isJokes = lane === "jokes";
  const isCrimes = lane === "crimes";

  useEffect(() => {
    setLoading(true);
    Promise.all([api.get("/products"), api.get("/collections")])
      .then(([pRes, cRes]) => {
        setProducts(pRes.data);
        setCollections(cRes.data || []);
      })
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let list = products;
    if (filterNew) list = list.filter((p) => p.featured);
    if (lane && LANE_CATEGORIES[lane]) {
      list = list.filter((p) => productLane(p) === lane);
    }
    return list;
  }, [products, filterNew, lane]);

  const clearLane = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("lane");
    setSearchParams(next, { replace: true });
  };

  const pageBg = theme?.page || "bg-[#F9F9F4]";
  const heroText = theme?.heroText || "text-[#0F0F0F]";

  return (
    <div className={`min-h-screen ${pageBg}`} data-testid="shop-page">
      <SEO
        title={filterNew ? "New Stuff" : "Shop"}
        path={filterNew ? "/shop/new" : "/shop"}
        description={
          filterNew
            ? "Fresh damp drops. Premium streetwear documenting modern leisure behaviour. New pieces from across the six Wet Cabbage divisions."
            : "Shop the entire Wet Cabbage catalogue. Premium streetwear organised into six divisions: Travel, Everyday Life, Pub, Sports & Leisure, Dad Life and Home."
        }
      />

      {/* HERO */}
      <section
        className={`relative overflow-hidden border-b-2 border-[#1A3626] py-16 md:py-24 ${heroText}`}
      >
        {/* Lane-specific decorative doodles */}
        {isJokes && (
          <div className="pointer-events-none absolute inset-0">
            <UpsettingDog className="absolute -bottom-2 right-[6%] hidden h-32 w-32 -rotate-[10deg] md:block lg:right-[10%] lg:h-44 lg:w-44" />
            <Scribble className="absolute right-[14%] top-[14%] hidden h-8 w-24 -rotate-[8deg] opacity-70 md:block" />
            <StarBlob className="absolute left-[2%] top-[36%] hidden h-12 w-12 rotate-[14deg] opacity-80 md:block lg:h-16 lg:w-16" />
          </div>
        )}
        {isCrimes && (
          <div className="pointer-events-none absolute inset-0">
            <BadFrog className="absolute right-[6%] top-1/2 hidden h-24 w-24 -translate-y-1/2 rotate-[8deg] md:block lg:right-[10%] lg:h-32 lg:w-32" />
            <HandArrow className="absolute left-[3%] top-[40%] hidden h-10 w-24 rotate-[20deg] md:block" />
            {/* Stamped Designed For Modern Leisure badge */}
            <div
              className="absolute right-[14%] top-[12%] hidden -rotate-[12deg] border-4 border-[#93C065] px-5 py-2 font-display text-xs font-black uppercase tracking-[0.3em] text-[#93C065] md:block"
              aria-hidden
            >
              ✦ Designed For Modern Leisure ✦
            </div>
          </div>
        )}

        <div className="relative mx-auto max-w-[1400px] px-6 md:px-10">
          <div
            className={`font-display text-xs font-bold uppercase tracking-[0.3em] ${theme?.kickerColor || ""}`}
          >
            {theme
              ? theme.kicker
              : filterNew
              ? "Fresh batches"
              : "The entire damp catalogue"}
          </div>
          <h1 className="font-display mt-3 text-5xl font-black tracking-tighter sm:text-7xl">
            {theme ? theme.title : filterNew ? "New stuff." : "Shop."}
          </h1>
          <div className="mt-3 max-w-md">
            <SquigglyUnderline
              className="h-4 w-full"
              stroke={theme?.underline || "#93C065"}
            />
          </div>
          <p className="mt-6 max-w-xl text-base md:text-lg">
            {theme
              ? theme.blurb
              : "Premium streetwear documenting modern leisure behaviour. Heavyweight blanks, hoodies, caps and accessories — fulfilled on demand, so nothing sits in a warehouse getting sad."}
          </p>

          {theme && (
            <div className="mt-8 flex flex-wrap items-center gap-3">
              {["damp", "crimes", "jokes"]
                .filter((l) => l !== lane)
                .map((l) => (
                  <Link
                    key={l}
                    to={`/shop?lane=${l}`}
                    className={`font-display inline-flex items-center gap-2 px-4 py-2.5 text-xs font-black uppercase tracking-widest transition-all ${
                      isJokes
                        ? "bg-[#0F0F0F] text-[#F9F9F4] hover:bg-[#1A3626]"
                        : "bg-[#93C065] text-[#0F0F0F] hover:bg-[#a9d27a]"
                    }`}
                    data-testid={`shop-switch-lane-${l}`}
                  >
                    Switch to {LANE_THEMES[l].titleShort}
                    <ArrowRight className="h-3.5 w-3.5" strokeWidth={3} />
                  </Link>
                ))}
              <button
                type="button"
                onClick={clearLane}
                className={`font-display inline-flex items-center gap-2 border-2 px-3 py-2 text-xs font-bold uppercase tracking-widest transition-all ${
                  isCrimes || isDamp
                    ? "border-[#F9F9F4] text-[#F9F9F4] hover:bg-[#F9F9F4]/10"
                    : "border-[#1A3626] text-[#0F0F0F] hover:bg-[#1A3626]/10"
                }`}
                data-testid="shop-clear-lane"
              >
                <X className="h-3.5 w-3.5" strokeWidth={3} />
                Show everything instead
              </button>
            </div>
          )}
        </div>
      </section>

      {/* LANE-SPECIFIC TICKER BANNER */}
      {theme && (
        <section
          className={`border-b-2 border-[#1A3626] py-3 ${theme.bannerBg}`}
          data-testid={`lane-banner-${lane}`}
        >
          <Marquee gradient={false} speed={70} pauseOnHover>
            {Array.from({ length: 4 }).map((_, i) => (
              <span
                key={i}
                className={`font-display mx-8 text-lg font-black uppercase tracking-tight md:text-2xl ${theme.bannerText}`}
              >
                {isJokes ? "★" : "✦"} {theme.bannerCopy}
              </span>
            ))}
          </Marquee>
        </section>
      )}

      {/* COLLECTION FILTER BAR — only when no lane is active */}
      {!theme && (
        <section className="sticky top-[73px] z-30 border-b-2 border-[#1A3626] bg-[#F9F9F4]/90 backdrop-blur-xl">
          <div className="mx-auto flex max-w-[1400px] items-center gap-2 overflow-x-auto px-6 py-4 md:px-10">
            <span
              className="font-display shrink-0 border-2 border-[#1A3626] bg-[#1A3626] px-4 py-2 text-xs font-bold uppercase tracking-widest text-[#F9F9F4]"
              data-testid="shop-filter-all"
            >
              Everything
            </span>
            {collections.map((c) => (
              <Link
                key={c.id}
                to={`/collections/${c.id}`}
                className="font-display shrink-0 border-2 border-[#1A3626] bg-transparent px-4 py-2 text-xs font-bold uppercase tracking-widest text-[#0F0F0F] transition-all hover:bg-[#EBE7DD]"
                data-testid={`shop-filter-${c.id}`}
              >
                {c.title}
                <ArrowRight className="ml-1 inline h-3 w-3" strokeWidth={3} />
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* PRODUCT GRID */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          {loading ? (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={i}
                  className={`aspect-[4/5] animate-pulse border-2 ${
                    isCrimes || isDamp ? "border-[#F9F9F4]" : "border-[#1A3626]"
                  } bg-[#EBE7DD]`}
                />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div
              className={`border-2 border-dashed p-16 text-center font-display font-bold uppercase tracking-wide ${
                isCrimes || isDamp ? "border-[#F9F9F4] text-[#F9F9F4]" : "border-[#1A3626]"
              }`}
            >
              Nothing here (yet). Try a different lane.
            </div>
          ) : (
            <div
              className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
              data-testid="shop-grid"
            >
              {filtered.map((p, i) => (
                <ProductCard
                  key={p.id}
                  product={p}
                  index={i}
                  theme={theme}
                  laneKey={lane}
                />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function ProductCard({ product, index, theme, laneKey }) {
  // Default styling matches the original neutral card.
  const cardBg = theme?.cardBg || "bg-[#EBE7DD]";
  const cardBorder = theme?.cardBorder || "border-[#1A3626]";
  const priceBg = theme?.priceBg || "bg-[#93C065]";
  const priceColor = theme?.priceColor || "text-[#0F0F0F]";
  const priceShape = theme?.priceShape || "";
  const priceRotate = theme?.priceRotate || "-rotate-3";
  const titleBg = theme?.titleBg || "bg-gradient-to-t from-[#0F0F0F] via-[#0F0F0F]/70 to-transparent";

  return (
    <Link
      to={`/shop/${product.id}`}
      className={`wc-card group relative block overflow-hidden wc-fade-up border-2 ${cardBorder} ${cardBg}`}
      style={{ animationDelay: `${index * 40}ms` }}
      data-testid={`shop-card-${product.id}`}
    >
      <div className="relative aspect-[4/5] overflow-hidden">
        <img
          src={product.image}
          alt={product.name}
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
          loading="lazy"
        />

        {/* Price tag — lane-themed */}
        <div
          className={`font-display absolute right-3 top-3 ${priceRotate} border-2 border-[#1A3626] ${priceBg} ${priceColor} ${priceShape} px-3 py-1 text-xs font-black tracking-widest`}
        >
          {product.currency === "USD" ? "$" : ""}
          {product.price.toFixed(0)}
          {product.currency && product.currency !== "USD"
            ? ` ${product.currency}`
            : ""}
        </div>

        {/* Lane-specific corner badge */}
        {laneKey === "damp" && (
          <div
            className="font-display absolute left-3 top-3 inline-flex items-center gap-1 border-2 border-[#1A3626] bg-[#93C065] px-2.5 py-1 text-[10px] font-black uppercase tracking-widest text-[#0F0F0F]"
            aria-hidden
          >
            <Sparkles className="h-3 w-3" strokeWidth={3} />
            Main Line
          </div>
        )}
        {laneKey === "jokes" && (
          <div
            className="font-display absolute left-3 top-3 inline-flex items-center gap-1 rounded-full border-2 border-[#1A3626] bg-[#F9F9F4] px-2.5 py-1 text-[10px] font-black uppercase tracking-widest text-[#0F0F0F]"
            aria-hidden
          >
            <Sparkles className="h-3 w-3" strokeWidth={3} />
            LOL
          </div>
        )}
        {laneKey === "crimes" && (
          <div
            className="font-display absolute left-3 top-3 inline-flex -rotate-[6deg] items-center gap-1 border-2 border-[#0F0F0F] bg-[#93C065] px-2 py-1 text-[10px] font-black uppercase tracking-[0.2em] text-[#0F0F0F]"
            aria-hidden
          >
            <Stamp className="h-3 w-3" strokeWidth={3} />
            Exhibit · {String(index + 1).padStart(2, "0")}
          </div>
        )}

        {/* Title bar */}
        <div className={`absolute inset-x-0 bottom-0 ${titleBg} p-4 pt-10`}>
          <div className="font-display line-clamp-2 text-sm font-bold uppercase leading-tight tracking-tight text-[#F9F9F4]">
            {product.name}
          </div>
        </div>
      </div>
    </Link>
  );
}
